/* ==========================================================================
   TLC · scroll reveal

   Every `[data-tlc-reveal]` group starts 24px low and transparent, then lifts
   into place the first time it comes near the fold. The styles live in
   tlc.css; this file only decides when each group has arrived.
   ========================================================================== */

(function tlcReveal() {
  // No observer, no reveal — and, because the flag below is never set, no
  // hidden state either. The page simply renders static.
  if (!('IntersectionObserver' in window)) return;

  // IMPORTANT: an observer, not a scroll handler. Above 990px base.css locks
  // <html>/<body> to the viewport and moves scrolling into `.page-wrapper`, so
  // `window` scroll events never fire on desktop. An observer is measured
  // against the viewport whichever element actually scrolls.
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        // One-way: a group that has arrived never hides again, so scrolling
        // back up does not replay it.
        observer.unobserve(entry.target);
      }
    },
    // Trimming 8% off the bottom of the viewport starts a group moving as its
    // top edge clears the fold, rather than once it is already fully on screen.
    { rootMargin: '0px 0px -8% 0px', threshold: 0 }
  );

  function observe(scope) {
    for (const element of scope.querySelectorAll('[data-tlc-reveal]')) {
      observer.observe(element);
    }
  }

  document.documentElement.setAttribute('data-tlc-reveal-ready', 'true');
  observe(document);

  // The theme editor replaces a section's markup wholesale on every setting
  // change, and the replacement has never been through the pass above.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) observe(event.target);
  });
})();

/* ==========================================================================
   TLC · collection grid "load more"

   The button is a real link to the next paginated page. Here it becomes an
   append: fetch that page, lift its cards into the grid already on screen, and
   move the button on to whatever comes after. Without this file the link still
   works, it just navigates.
   ========================================================================== */

(function tlcLoadMore() {
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-tlc-load-more]');
    if (!button || button.dataset.tlcBusy === 'true') return;

    const grid = document.querySelector('[data-tlc-shop-grid]');
    if (!grid) return;

    event.preventDefault();
    button.dataset.tlcBusy = 'true';
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(button.href);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const next = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = next.querySelector('[data-tlc-shop-grid]');
      if (!nextGrid) throw new Error('no grid on the next page');

      // The cards are moved rather than copied so their markup — and any
      // srcset the theme generated — arrives exactly as the server wrote it.
      const added = Array.from(nextGrid.children);
      added.forEach((card) => grid.appendChild(card));

      // The next page's own count already reads "showing 24 of 104", which is
      // the state the page is in once its cards have landed here.
      const count = document.querySelector('[data-tlc-shop-count]');
      const nextCount = next.querySelector('[data-tlc-shop-count]');
      if (count && nextCount) count.textContent = nextCount.textContent;

      const nextButton = next.querySelector('[data-tlc-load-more]');
      if (nextButton) {
        button.href = nextButton.href;
      } else {
        // Nothing left to load. Focus moves to the first card that just
        // arrived, so keyboard users are not dropped at the end of the
        // document when the control they were on disappears.
        added[0]?.querySelector('a')?.focus();
        button.remove();
      }
    } catch (error) {
      // Put the user back on the path that always works.
      console.error('[tlc] load more failed, falling back to navigation', error);
      window.location.href = button.href;
    } finally {
      button.dataset.tlcBusy = 'false';
      button.removeAttribute('aria-busy');
    }
  });
})();
